import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AdminPermission } from '@enums/admin-permission.enum';
import {
  ADMIN_FLEET_REGISTRY_BATCHES,
  ADMIN_FLEET_REGISTRY_BATCH_ROWS,
  ADMIN_SUBMIT_FLEET_REGISTRY_BATCH,
} from '@gql_queries/taxi_fleet/registry_batch';
import { AdminPermissionService } from '@services/admin-permission.service';
import {
  FleetRegistryBatchPreview,
  FleetRegistryBatchPreviewRow,
  FleetRegistryBatchRowAssessment,
  FleetRegistryBatchService,
} from '@services/fleet-registry-batch.service';
import { isMissingPermissionError, MISSING_PERMISSION_MESSAGE } from '@shared/utils/admin-permission-error.util';

type BatchStatus = 'for_processing' | 'processing' | 'failed' | 'success';
type PreviewFilter = 'ALL' | FleetRegistryBatchRowAssessment;
type RowStatusFilter = 'ALL' | BatchStatus;

interface FleetRegistryBatch {
  fleetRegistryBatchId: string;
  fileName: string;
  status: BatchStatus;
  totalRows: number;
  successRows: number;
  failedRows: number;
  failureReason?: string | null;
  adminName: string;
  dateStarted?: string | null;
  dateCompleted?: string | null;
  dateCreated: string;
}

interface FleetRegistryBatchRow {
  fleetRegistryBatchRowId: string;
  rowNumber: number;
  plateNumber?: string | null;
  conductionNumber?: string | null;
  vehicleMaker: string;
  vehicleModel: string;
  yearModel: number;
  vehicleId?: string | null;
  driverFirstName?: string | null;
  driverLastName?: string | null;
  driverMobile?: string | null;
  status: BatchStatus;
  failureReason?: string | null;
}


const PREVIEW_PAGE_SIZE = 10;
const BATCH_PAGE_SIZE = 10;
const ROWS_PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 4000;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  for_processing: 'For Processing',
  processing: 'Processing',
  failed: 'Failed',
  success: 'Success',
};

const BATCH_STATUS_CLASSES: Record<BatchStatus, string> = {
  for_processing: 'stat-pending',
  processing: 'stat-approval',
  failed: 'stat-inactive',
  success: 'stat-active',
};

const PREVIEW_STATUS_LABELS: Record<FleetRegistryBatchRowAssessment, string> = {
  NO_DUPLICATE: 'No-Duplicate',
  HAS_DUPLICATE: 'Has-Duplicate',
  INVALID: 'Invalid',
};

const PREVIEW_STATUS_CLASSES: Record<FleetRegistryBatchRowAssessment, string> = {
  NO_DUPLICATE: 'stat-active',
  HAS_DUPLICATE: 'stat-inactive',
  INVALID: 'stat-approval',
};

/**
 * Batch upload on the Fleet Registry tab: download the template, upload a
 * filled-in file for a duplicate check (nothing is saved), review the rows,
 * then submit the No-Duplicate ones. The API queues the batch and registers
 * the vehicles in the background; the batch list below tracks each upload
 * (parent) and its rows (children).
 */
@Component({
  selector: 'app-fleet-registry-batch',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fleet-registry-batch.component.html',
  styleUrl: './fleet-registry-batch.component.css',
})
export class FleetRegistryBatchComponent implements OnInit, OnDestroy {
  /** Hashed fleet id from the route. */
  @Input({ required: true }) fleetId = '';
  /** Emitted when a batch finishes, so the registry list can reload. */
  @Output() registryChanged = new EventEmitter<void>();

  isDownloadingTemplate = false;
  isValidating = false;
  isSubmitting = false;
  uploadError = '';

  preview: FleetRegistryBatchPreview | null = null;
  previewFilter: PreviewFilter = 'ALL';
  previewPage = 1;

  batches: FleetRegistryBatch[] = [];
  batchPage = 1;
  batchTotalPages = 1;
  batchTotal = 0;
  isBatchesLoading = false;
  batchesError = '';

  /** Batch shown in the View Details modal and its rows (server-paginated). */
  selectedBatch: FleetRegistryBatch | null = null;
  detailRows: FleetRegistryBatchRow[] = [];
  detailPage = 1;
  detailTotalPages = 1;
  detailTotal = 0;
  detailStatusFilter: RowStatusFilter = 'ALL';
  isDetailLoading = false;
  detailError = '';
  readonly rowStatusFilters: { value: RowStatusFilter; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
    { value: 'processing', label: 'Processing' },
    { value: 'for_processing', label: 'For Processing' },
  ];

  readonly previewStatusLabels = PREVIEW_STATUS_LABELS;
  readonly previewStatusClasses = PREVIEW_STATUS_CLASSES;
  readonly filters: { value: PreviewFilter; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'NO_DUPLICATE', label: 'No-Duplicate' },
    { value: 'HAS_DUPLICATE', label: 'Has-Duplicate' },
    { value: 'INVALID', label: 'Invalid' },
  ];

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private confirmModalRef: NgbModalRef | null = null;
  private detailModalRef: NgbModalRef | null = null;

  constructor(
    private apollo: Apollo,
    private batchService: FleetRegistryBatchService,
    private permissions: AdminPermissionService,
    private modalService: NgbModal,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    void this.loadBatches();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
  }

  get canManage(): boolean {
    return this.permissions.can(AdminPermission.FleetsManage);
  }

  /* ----------------------------- template + upload ----------------------------- */

  downloadTemplate(): void {
    if (this.isDownloadingTemplate) {
      return;
    }
    this.isDownloadingTemplate = true;
    this.batchService.downloadTemplate().subscribe({
      next: (blob) => {
        saveAs(blob, 'fleet-vehicle-registry-template.xlsx');
        this.isDownloadingTemplate = false;
      },
      error: (error) => {
        this.isDownloadingTemplate = false;
        this.toastr.error(this.errorMessage(error, 'Unable to download the template.'));
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so choosing the same file again still triggers a change.
    input.value = '';
    if (!file) {
      return;
    }
    this.uploadError = '';
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.uploadError = 'Upload the .xlsx template.';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.uploadError = 'The file is larger than 5 MB.';
      return;
    }

    this.isValidating = true;
    this.preview = null;
    this.batchService.validate(this.fleetId, file).subscribe({
      next: (preview) => {
        this.isValidating = false;
        this.preview = preview;
        this.previewFilter = 'ALL';
        this.previewPage = 1;
      },
      error: (error) => {
        this.isValidating = false;
        this.uploadError = this.errorMessage(error, 'Unable to read the file.');
      },
    });
  }

  clearPreview(): void {
    this.preview = null;
    this.uploadError = '';
  }

  /* --------------------------------- preview --------------------------------- */

  get filteredPreviewRows(): FleetRegistryBatchPreviewRow[] {
    const rows = this.preview?.rows || [];
    return this.previewFilter === 'ALL' ? rows : rows.filter((row) => row.status === this.previewFilter);
  }

  get pagedPreviewRows(): FleetRegistryBatchPreviewRow[] {
    const start = (this.previewPage - 1) * PREVIEW_PAGE_SIZE;
    return this.filteredPreviewRows.slice(start, start + PREVIEW_PAGE_SIZE);
  }

  get previewTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredPreviewRows.length / PREVIEW_PAGE_SIZE));
  }

  get previewPageNumbers(): number[] {
    return this.pageWindow(this.previewPage, this.previewTotalPages);
  }

  get invalidMobileRows(): number {
    return this.preview?.invalidMobileRows || 0;
  }

  get submittableRows(): FleetRegistryBatchPreviewRow[] {
    return (this.preview?.rows || []).filter((row) => row.status === 'NO_DUPLICATE');
  }

  get exceedsVehicleLimit(): boolean {
    return !!this.preview && this.submittableRows.length > this.preview.remainingVehicles;
  }

  get canSubmit(): boolean {
    return this.canManage && this.submittableRows.length > 0 && !this.exceedsVehicleLimit && !this.isSubmitting;
  }

  setPreviewFilter(filter: PreviewFilter): void {
    this.previewFilter = filter;
    this.previewPage = 1;
  }

  goToPreviewPage(page: number): void {
    this.previewPage = Math.min(Math.max(page, 1), this.previewTotalPages);
  }

  countFor(filter: PreviewFilter): number {
    if (!this.preview) {
      return 0;
    }
    switch (filter) {
      case 'NO_DUPLICATE':
        return this.preview.noDuplicateRows;
      case 'HAS_DUPLICATE':
        return this.preview.hasDuplicateRows;
      case 'INVALID':
        return this.preview.invalidRows;
      default:
        return this.preview.totalRows;
    }
  }

  previewVehicle(row: FleetRegistryBatchPreviewRow): string {
    const values = row.values;
    return [values.vehicleMaker, values.vehicleModel, values.yearModel].filter(Boolean).join(' / ') || '—';
  }

  previewDriver(row: FleetRegistryBatchPreviewRow): string {
    const values = row.values;
    const name = [values.driverFirstName, values.driverLastName].filter(Boolean).join(' ');
    if (!name && !values.driverMobile) {
      return '—';
    }
    return [name, values.driverMobile].filter(Boolean).join(' · ');
  }

  openSubmitConfirm(modal: TemplateRef<unknown>): void {
    if (!this.canSubmit) {
      return;
    }
    this.confirmModalRef = this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  submit(): void {
    if (!this.preview || !this.canSubmit) {
      return;
    }
    const preview = this.preview;
    const rows = this.submittableRows.map((row) => {
      const values: Record<string, string> = {};
      Object.entries(row.values).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          values[key] = String(value);
        }
      });
      return { rowNumber: row.rowNumber, ...values };
    });

    this.isSubmitting = true;
    this.apollo
      .mutate({
        mutation: ADMIN_SUBMIT_FLEET_REGISTRY_BATCH,
        variables: { input: { fleetId: preview.fleetId, fileName: preview.fileName, rows } },
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.confirmModalRef?.close();
          this.preview = null;
          this.toastr.success(`${rows.length} vehicle${rows.length === 1 ? '' : 's'} queued for registration.`);
          this.batchPage = 1;
          void this.loadBatches();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.confirmModalRef?.close();
          this.toastr.error(this.errorMessage(error, 'Unable to submit the batch.'));
        },
      });
  }

  /* --------------------------------- batches --------------------------------- */

  async loadBatches(silent = false): Promise<void> {
    if (!this.fleetId) {
      return;
    }
    if (!silent) {
      this.isBatchesLoading = true;
      this.batchesError = '';
    }
    const previous = new Map(this.batches.map((batch) => [batch.fleetRegistryBatchId, batch.status]));
    try {
      const result: any = await firstValueFrom(
        this.apollo.query({
          fetchPolicy: 'network-only',
          query: ADMIN_FLEET_REGISTRY_BATCHES,
          variables: { input: { fleetId: this.fleetId, page: this.batchPage, limit: BATCH_PAGE_SIZE } },
        }),
      );
      const payload = result?.data?.adminFleetRegistryBatches;
      this.batches = (payload?.items || []) as FleetRegistryBatch[];
      this.batchTotal = Number(payload?.total) || 0;
      this.batchTotalPages = Math.max(1, Number(payload?.totalPages) || 1);
      this.batchesError = '';

      const finishedNow = this.batches.filter(
        (batch) => this.isFinished(batch.status) && previous.has(batch.fleetRegistryBatchId) && !this.isFinished(previous.get(batch.fleetRegistryBatchId)!),
      );
      if (finishedNow.length > 0) {
        this.registryChanged.emit();
      }
      this.refreshOpenDetails(previous);
    } catch (error) {
      if (!silent) {
        this.batches = [];
        this.batchesError = this.errorMessage(error, 'Unable to load batch uploads.');
      }
    } finally {
      this.isBatchesLoading = false;
      this.schedulePoll();
    }
  }

  goToBatchPage(page: number): void {
    const next = Math.min(Math.max(page, 1), this.batchTotalPages);
    if (next === this.batchPage) {
      return;
    }
    this.batchPage = next;
    void this.loadBatches();
  }

  /* ------------------------------ batch details ------------------------------ */

  openBatchDetails(batch: FleetRegistryBatch, modal: TemplateRef<unknown>): void {
    this.selectedBatch = batch;
    this.detailRows = [];
    this.detailPage = 1;
    this.detailTotalPages = 1;
    this.detailTotal = 0;
    this.detailStatusFilter = 'ALL';
    this.detailError = '';
    this.detailModalRef = this.modalService.open(modal, { size: 'xl', scrollable: true });
    this.detailModalRef.result.finally(() => {
      this.detailModalRef = null;
      this.selectedBatch = null;
    }).catch(() => undefined);
    void this.loadDetailRows(1);
  }

  setDetailStatusFilter(filter: RowStatusFilter): void {
    if (filter === this.detailStatusFilter) {
      return;
    }
    this.detailStatusFilter = filter;
    void this.loadDetailRows(1);
  }

  goToDetailPage(page: number): void {
    const next = Math.min(Math.max(page, 1), this.detailTotalPages);
    if (next !== this.detailPage) {
      void this.loadDetailRows(next);
    }
  }

  get detailPageNumbers(): number[] {
    return this.pageWindow(this.detailPage, this.detailTotalPages);
  }

  async loadDetailRows(page: number): Promise<void> {
    const batch = this.selectedBatch;
    if (!batch) {
      return;
    }
    this.isDetailLoading = true;
    this.detailError = '';
    try {
      const result: any = await firstValueFrom(
        this.apollo.query({
          fetchPolicy: 'network-only',
          query: ADMIN_FLEET_REGISTRY_BATCH_ROWS,
          variables: {
            input: {
              fleetRegistryBatchId: batch.fleetRegistryBatchId,
              page,
              limit: ROWS_PAGE_SIZE,
              ...(this.detailStatusFilter === 'ALL' ? {} : { status: this.detailStatusFilter }),
            },
          },
        }),
      );
      // The modal may have been closed or switched to another batch meanwhile.
      if (this.selectedBatch?.fleetRegistryBatchId !== batch.fleetRegistryBatchId) {
        return;
      }
      const payload = result?.data?.adminFleetRegistryBatchRows;
      this.detailRows = (payload?.items || []) as FleetRegistryBatchRow[];
      this.detailPage = page;
      this.detailTotal = Number(payload?.total) || 0;
      this.detailTotalPages = Math.max(1, Number(payload?.totalPages) || 1);
    } catch (error) {
      this.detailError = this.errorMessage(error, 'Unable to load the rows.');
    } finally {
      this.isDetailLoading = false;
    }
  }

  /** Keeps an open modal in step with the polled list while its batch runs. */
  private refreshOpenDetails(previous: Map<string, BatchStatus>): void {
    if (!this.selectedBatch) {
      return;
    }
    const latest = this.batches.find((batch) => batch.fleetRegistryBatchId === this.selectedBatch!.fleetRegistryBatchId);
    if (!latest) {
      return;
    }
    const changed = previous.get(latest.fleetRegistryBatchId) !== latest.status;
    this.selectedBatch = latest;
    if (changed || !this.isFinished(latest.status)) {
      void this.loadDetailRows(this.detailPage);
    }
  }

  batchStatusLabel(status: BatchStatus): string {
    return BATCH_STATUS_LABELS[status] || status;
  }

  batchStatusClass(status: BatchStatus): string {
    return BATCH_STATUS_CLASSES[status] || 'stat-pending';
  }

  rowVehicle(row: FleetRegistryBatchRow): string {
    return [row.vehicleMaker, row.vehicleModel, row.yearModel].filter(Boolean).join(' / ');
  }

  rowDriver(row: FleetRegistryBatchRow): string {
    const name = [row.driverFirstName, row.driverLastName].filter(Boolean).join(' ');
    return [name, row.driverMobile].filter(Boolean).join(' · ') || '—';
  }

  pageWindow(current: number, total: number): number[] {
    const start = Math.max(1, Math.min(current - 2, total - 4));
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  /* --------------------------------- helpers --------------------------------- */

  private isFinished(status: BatchStatus): boolean {
    return status === 'success' || status === 'failed';
  }

  /** Polls while a batch on this page is still queued or running. */
  private schedulePoll(): void {
    this.stopPolling();
    if (this.destroyed || !this.batches.some((batch) => !this.isFinished(batch.status))) {
      return;
    }
    this.pollTimer = setTimeout(() => void this.loadBatches(true), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (isMissingPermissionError(error)) {
      return MISSING_PERMISSION_MESSAGE;
    }
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message;
      if (typeof message === 'string' && message) {
        return message;
      }
      if (Array.isArray(message) && message.length) {
        return message.join(' ');
      }
      if (error.status === 413) {
        return 'The file is too large.';
      }
      return fallback;
    }
    const graphQLMessage = (error as any)?.errors?.[0]?.message || (error as any)?.graphQLErrors?.[0]?.message;
    return graphQLMessage || (error as any)?.message || fallback;
  }
}
